import { subscriptionService } from "@miplatform/api";
import { GLOBAL_HUB_NAME } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  SignalrHub,
  createHub,
  findHubByName,
  removeHubByName,
  subscribeToGlobalSignalrMessage,
  subscribeToHubStateChange,
} from "@miplatform/signalr";
import { HubStatuses, IGlobalSignalrMessagePayload } from "@miplatform/types";
import { bugsnag, createUUID } from "@miplatform/utils";
import { useCallback, useRef, useState } from "react";

const startUUID = createUUID();
const stopUUID = createUUID();

export const useGlobalSignalR = ({
  onGlobalMessageReceived,
  onHubStateChange,
  onStart,
  onStop,
  hubName = GLOBAL_HUB_NAME,
}: UseSignalRPayload): UseSignalRReturn => {
  const hubRef = useRef<SignalrHub>(null);
  const unsubscribeRef = useRef<() => void>(null);
  const [hubState, setHubState] = useState<HubStatuses | null>(null);
  const stateManager = useComponentState();

  const getGlobalNegotiationOptions = useCallback(async () => {
    const negotiationOptions = await subscriptionService.negotiation.global();
    if (!negotiationOptions) {
      bugsnag.setContext("useSignalR::getGlobalNegotiationOptions");
      bugsnag.notify("Negotiation options not found");
      return null;
    }
    return negotiationOptions;
  }, []);

  // Handle incoming global SignalR messages, which may be buffered and received as an array of payloads in string format.
  const onGlobalMessageReceivedInternal = (payloads: string[]) => {
    payloads.forEach((payload) => {
      onGlobalMessageReceived?.(JSON.parse(payload));
    });
  };
 
  const onHubStateChangeInternal = useCallback((state: HubStatuses) => {
    setHubState(state);

    onHubStateChange?.(state);
  }, []);

  const subscribe = useCallback(() => {
    let subscriptions: { unsubscribe: () => void }[];

    if (hubRef.current) {
      subscriptions = [
        subscribeToHubStateChange(hubRef.current, onHubStateChangeInternal),
        subscribeToGlobalSignalrMessage(hubRef.current, onGlobalMessageReceivedInternal)
      ];
    }

    return () => {
      if (subscriptions) {
        subscriptions.forEach((subs) => {
          if (subs && typeof subs.unsubscribe === "function") {
            subs.unsubscribe();
          }
        });
      }
    };
  }, []);

  const start = useCallback(() => {
    stateManager.abort(startUUID);
    return stateManager.run(async () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

        hubRef.current = findHubByName(hubName) ?? null;

        if (!hubRef.current) {
            const options = await getGlobalNegotiationOptions();

            if (!options) {
                bugsnag.setContext("useSignalR::start");
                bugsnag.notify(`Failed to get options for global hub`);
                return;
            }

            hubRef.current = createHub(
                hubName,
                options?.data.url?.replace("signalr-emulator", "localhost"),
                {
                    accessTokenFactory: async () => {
                        const opts = await getGlobalNegotiationOptions();
                        return opts?.data?.accessToken;
                    },
                },
                true
            );
        }
      hubRef.current.start();
      onStart?.();
      unsubscribeRef.current = subscribe();
    }, startUUID);
  }, []);

  const stop = useCallback(() => {
    stateManager.abort(stopUUID);
    return stateManager.run(() => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (hubRef.current) {
        hubRef.current.stop?.();
        removeHubByName(hubName);
        hubRef.current = null;
      }

      setHubState(HubStatuses.Disconnected);

      onStop?.();
    }, stopUUID);
  }, []);

  return {
    start,
    stop,
    connectionStatus: hubState ?? HubStatuses.Disconnected,
    isLoading: stateManager.isBusy(),
  };
};

type UseSignalRPayload = {
  onGlobalMessageReceived?: (payload: IGlobalSignalrMessagePayload) => void;
  onHubStateChange?: (payload: HubStatuses) => void;
  onStart?: () => void;
  onStop?: () => void;
  dependencies?: unknown[];
  hubName?: string;
};

type UseSignalRReturn = {
  start: () => void;
  stop: () => void;
  connectionStatus: HubStatuses;
  isLoading: boolean;
};
